import type { AxiosError } from 'axios';
import { authClient } from './client';
import type {
  CalendarDayLoad,
  ConsignorPaymentsPayload,
  ConsignorPaymentsQuery,
  PaymentChannel,
  PaymentLineItem,
  PaymentRow,
} from '../types/consignorPayments';

// ─── Parsers ──────────────────────────────────────────────────────────────────

const VALID_CHANNELS: PaymentChannel[] = ['cash', 'store_credit', 'transfer'];

function parseLineItem(raw: unknown): PaymentLineItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id) || id <= 0) return null;
  const artIdRaw = r.artId ?? r.art_id;
  const imageUrlRaw = r.imageUrl ?? r.image_url ?? r.thumbnailUrl;
  return {
    id,
    artId: typeof artIdRaw === 'string' && artIdRaw.trim() ? artIdRaw.trim() : null,
    productName: String(r.productName ?? r.product_name ?? '').trim(),
    variantName: String(r.variantName ?? r.variant_name ?? '').trim(),
    imageUrl: typeof imageUrlRaw === 'string' && imageUrlRaw.trim() ? imageUrlRaw.trim() : null,
    cost: Number(r.cost ?? 0),
    soldAmount: Number(r.soldAmount ?? r.sold_amount ?? 0),
    status: String(r.status ?? '').trim(),
    channel: typeof r.channel === 'string' && r.channel ? r.channel : null,
  };
}

function parsePaymentRow(raw: unknown): PaymentRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id) || id <= 0) return null;

  const channelRaw = String(r.channel ?? '')
    .toLowerCase()
    .replace(/[^a-z_]/g, '');
  const channel: PaymentChannel = VALID_CHANNELS.includes(channelRaw as PaymentChannel)
    ? (channelRaw as PaymentChannel)
    : 'transfer';

  const scheduledDateRaw = r.scheduledDate ?? r.scheduled_date;
  const rawItems = r.items;

  return {
    id,
    consignorName: String(r.consignorName ?? r.consignor_name ?? '').trim(),
    scheduledDate:
      typeof scheduledDateRaw === 'string' && scheduledDateRaw.trim()
        ? scheduledDateRaw.trim()
        : null,
    amount: Number(r.amount ?? 0),
    channel,
    paid: Boolean(r.paid),
    items: Array.isArray(rawItems)
      ? rawItems.map(parseLineItem).filter((x): x is PaymentLineItem => x !== null)
      : [],
  };
}

function parsePayload(
  body: unknown,
  fallbackPage: number,
  fallbackPageSize: number
): ConsignorPaymentsPayload {
  const empty: ConsignorPaymentsPayload = {
    items: [],
    total: 0,
    page: fallbackPage,
    pageSize: fallbackPageSize,
  };
  if (!body || typeof body !== 'object') return empty;
  const o = body as Record<string, unknown>;
  const inner = (o.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
    string,
    unknown
  >;
  const rawItems = inner.items;
  const items = Array.isArray(rawItems)
    ? rawItems.map(parsePaymentRow).filter((x): x is PaymentRow => x !== null)
    : [];
  const total =
    typeof inner.total === 'number' && Number.isFinite(inner.total) ? inner.total : items.length;
  const page = typeof inner.page === 'number' && inner.page >= 1 ? inner.page : fallbackPage;
  const pageSize =
    typeof inner.pageSize === 'number' && inner.pageSize >= 1 ? inner.pageSize : fallbackPageSize;
  return { items, total, page, pageSize };
}

// ─── Query builder ────────────────────────────────────────────────────────────

function buildParams(query: ConsignorPaymentsQuery): Record<string, string | number> {
  const p: Record<string, string | number> = {};
  if (query.page) p.page = query.page;
  if (query.pageSize) p.pageSize = query.pageSize;
  if (query.orderBy) p.orderBy = query.orderBy;
  if (query.orderDirection) p.orderDirection = query.orderDirection;
  if (query.channel) p.channel = query.channel;
  if (query.paid !== undefined) p.paid = String(query.paid);
  if (query.pendingToSchedule) p.pendingToSchedule = 'true';
  if (query.dueTodayOrOverdue) p.dueTodayOrOverdue = 'true';
  return p;
}

// ─── Exported functions ───────────────────────────────────────────────────────

export type ConsignorPaymentsResult =
  | { ok: true; data: ConsignorPaymentsPayload }
  | { ok: false; error: string };

/**
 * GET /api/admin/consignor-payments
 *
 * Returns a paginated, filterable list of consignor payment batches.
 * Each row includes the payment metadata and the sale line items.
 */
export async function fetchConsignorPayments(
  query: ConsignorPaymentsQuery = {}
): Promise<ConsignorPaymentsResult> {
  const page = query.page ?? 1;
  const pageSize = query.pageSize ?? 25;
  try {
    const { data } = await authClient.get<unknown>('/api/admin/consignor-payments', {
      params: buildParams({ ...query, page, pageSize }),
    });
    return { ok: true, data: parsePayload(data, page, pageSize) };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    return {
      ok: false,
      error: ax.response?.data?.message ?? 'No se pudo cargar la lista de pagos.',
    };
  }
}

export type CalendarLoadResult =
  | { ok: true; data: CalendarDayLoad[] }
  | { ok: false; error: string };

/**
 * GET /api/admin/consignor-payments/calendar
 *
 * Returns each day in the requested month that already has ≥1 scheduled
 * (unpaid) payment assigned to it, along with the summed payout amount.
 * Days with no scheduled payments are omitted from the array.
 *
 * Query params:
 *   year  – full 4-digit year, e.g. 2024
 *   month – 1-indexed month, 1–12
 *
 * Response 200:
 * {
 *   "data": [
 *     { "date": "2024-10-05", "amount": 2340.00 },
 *     { "date": "2024-10-12", "amount": 1750.00 }
 *   ]
 * }
 *
 * Errors: 400 (invalid year/month), 403, 500.
 */
export async function fetchPaymentCalendarLoad(
  year: number,
  month: number // 1-indexed
): Promise<CalendarLoadResult> {
  try {
    const { data } = await authClient.get<unknown>('/api/admin/consignor-payments/calendar', {
      params: { year, month },
    });
    const body = data as Record<string, unknown>;
    const inner = body.data !== undefined ? body.data : body;
    const raw = Array.isArray(inner) ? inner : [];
    const result: CalendarDayLoad[] = raw
      .map((item: unknown) => {
        if (!item || typeof item !== 'object') return null;
        const r = item as Record<string, unknown>;
        const date = String(r.date ?? '').trim();
        const amount = Number(r.amount ?? 0);
        if (!date) return null;
        return { date, amount: Number.isFinite(amount) ? amount : 0 };
      })
      .filter((x): x is CalendarDayLoad => x !== null);
    return { ok: true, data: result };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    return {
      ok: false,
      error: ax.response?.data?.message ?? 'No se pudo cargar el calendario de pagos.',
    };
  }
}

export type ScheduleDateResult = { ok: true } | { ok: false; error: string };

/**
 * PATCH /api/admin/consignor-payments/:id/schedule
 *
 * Sets (or updates) the scheduled payout date for a payment batch.
 * Can be called repeatedly to change the date before the batch is marked paid.
 *
 * Request body:
 * { "scheduledDate": "2024-10-15" }   // ISO date string, date-only (no time)
 *
 * Response 200: { "message": "OK" }
 *
 * Errors:
 *   400 – missing or invalid scheduledDate format.
 *   404 – payment batch not found.
 *   409 – payment is already marked paid (date cannot change).
 *   500 – server error.
 */
export async function schedulePaymentDate(
  paymentId: number,
  scheduledDate: string
): Promise<ScheduleDateResult> {
  try {
    await authClient.patch(`/api/admin/consignor-payments/${paymentId}/schedule`, {
      scheduledDate,
    });
    return { ok: true };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    return {
      ok: false,
      error: ax.response?.data?.message ?? 'No se pudo programar la fecha de pago.',
    };
  }
}

export type MarkPaidResult = { ok: true } | { ok: false; error: string };

/**
 * PATCH /api/admin/consignor-payments/:id/mark-paid
 *
 * Marks a consignor payment batch as paid. The server records the current
 * timestamp as the paid-at date.
 *
 * Request: no body (or empty JSON object {})
 * Response 200: { "message": "OK" }
 * Errors: 404 not found, 409 already paid, 500 server error.
 */
export async function markPaymentPaid(paymentId: number): Promise<MarkPaidResult> {
  try {
    await authClient.patch(`/api/admin/consignor-payments/${paymentId}/mark-paid`, {});
    return { ok: true };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    return {
      ok: false,
      error: ax.response?.data?.message ?? 'No se pudo marcar el pago como realizado.',
    };
  }
}
